// Copyright (c) Ping. All rights reserved.
//
// Fires Activity reminders locally. Reminders are synced across the user's
// devices over the DeviceGroup (see `deviceGroupReminderSync.ts`), but the
// actual "fire" is a local, per-device concern: whichever device is awake at
// `fireAt` raises a notification. We persist `firedAt` on the row so a reminder
// is not re-fired on reload, and so a sibling device that already fired it does
// not double-notify (the firedAt write is local-only, not synced — at most one
// extra notification across devices, which is acceptable for a reminder).
//
// `setTimeout` is capped at ~24.8 days (2^31-1 ms); for reminders further out we
// re-arm on the next list change / a daily tick. Reminders already past `fireAt`
// on mount fire immediately (catch-up after the app was closed).

import { useEffect } from 'react';

import { log } from '@core/log/log';
import { webRemindersPersister } from '@core/persistence/db';

const MAX_TIMEOUT_MS = 2_147_483_647; // 2^31 - 1

function notify(title: string, body: string): void {
  try {
    if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
       
      new Notification(title, { body });
      return;
    }
  } catch {
    /* fall through to log */
  }
  log.info({ event: 'reminders.fire', title });
}

/**
 * Watches the reminders store and fires due reminders. Mount once (e.g. in the
 * AppShell or the Activity host). `onFire` is invoked with the reminder so the
 * caller can additionally raise the activity / open the conversation.
 */
export function useReminderScheduler(
  onFire?: (reminder: { id: string; conversationId: string; messageId?: string; note?: string }) => void,
): void {
  useEffect(() => {
    const timers = new Map<string, ReturnType<typeof setTimeout>>();
    let disposed = false;

    const fire = (r: {
      id: string;
      conversationId: string;
      messageId?: string;
      fireAt: number;
      note?: string;
      createdAt: number;
    }): void => {
      if (disposed) return;
      // Mark fired first (idempotent) so a re-projection doesn't re-arm it.
      void webRemindersPersister.put({ ...r, firedAt: Date.now() });
      notify('Reminder', r.note && r.note.length > 0 ? r.note : 'You asked to be reminded about this.');
      onFire?.({
        id: r.id,
        conversationId: r.conversationId,
        ...(r.messageId !== undefined ? { messageId: r.messageId } : {}),
        ...(r.note !== undefined ? { note: r.note } : {}),
      });
    };

    const arm = (rows: readonly { id: string; conversationId: string; messageId?: string; fireAt: number; note?: string; createdAt: number; firedAt?: number }[]): void => {
      for (const t of timers.values()) clearTimeout(t);
      timers.clear();
      const now = Date.now();
      for (const r of rows) {
        if (r.firedAt !== undefined) continue; // already fired
        const delay = r.fireAt - now;
        if (delay <= 0) {
          fire(r);
        } else if (delay <= MAX_TIMEOUT_MS) {
          timers.set(
            r.id,
            setTimeout(() => fire(r), delay),
          );
        }
        // Reminders > ~24 days out are re-armed on the next list change.
      }
    };

    let unsubscribe: (() => void) | undefined;
    if (webRemindersPersister.subscribe) {
      unsubscribe = webRemindersPersister.subscribe((rows) => arm(rows));
    } else {
      void webRemindersPersister.list().then((rows) => arm(rows));
    }

    return () => {
      disposed = true;
      for (const t of timers.values()) clearTimeout(t);
      timers.clear();
      unsubscribe?.();
    };
  }, [onFire]);
}
